import { useQuery } from "@tanstack/react-query";
import axios from "src/util/axios";
import Token from "src/util/Token";
import {
  getTeamUrl,
  failureStatsUrl,
  failedTransactionsUrl,
} from "src/react-query/endPoints";
import { useSelectedTeam } from "./useSelectedTeam";

/**
 * Failure-rate analytics for the Failed Transactions view, scoped to the
 * selected team/company (and its descendants). `dateRange` is a named range
 * (last7days/last30days/last60days/last90days) or pass explicit from/to
 * (YYYY-MM-DD). Returns { summary, breakdownByCharger }.
 */
export const useFailureStats = ({ dateRange, from, to } = {}) => {
  const { teamId } = useSelectedTeam();
  return useQuery(
    ["failureStats", teamId, dateRange, from, to],
    async () => {
      Token.setAuthToken();
      const params = {};
      if (dateRange) params.dateRange = dateRange;
      if (from) params.from = from;
      if (to) params.to = to;
      const { data } = await axios.get(getTeamUrl(teamId, failureStatsUrl), {
        params,
      });
      return data?.data ?? null;
    },
    { enabled: !!teamId, staleTime: 60 * 1000, keepPreviousData: true }
  );
};

/**
 * Paginated drill-down list of individual failed transactions (0-kWh sessions +
 * orphan payments). `type` ('zero_kwh' | 'orphan') narrows to one population.
 * `page` is 0-based (DataGrid convention); the API is 1-based.
 */
export const useFailedTransactionsList = ({
  dateRange,
  type,
  chargepointId,
  search,
  page = 0,
  pageSize = 25,
} = {}) => {
  const { teamId } = useSelectedTeam();
  return useQuery(
    ["failedTransactionsList", teamId, dateRange, type, chargepointId, search, page, pageSize],
    async () => {
      Token.setAuthToken();
      const params = { page: page + 1, limit: pageSize };
      if (dateRange) params.dateRange = dateRange;
      if (type) params.type = type;
      if (chargepointId) params.chargepointId = chargepointId;
      if (search) params.search = search;
      const { data } = await axios.get(
        getTeamUrl(teamId, failedTransactionsUrl),
        { params }
      );
      return { data: data?.data ?? [], total: data?.total ?? 0 };
    },
    { enabled: !!teamId, keepPreviousData: true, staleTime: 60 * 1000 }
  );
};

export default useFailureStats;
